import { useState } from "react";
import { MapPin, Phone, MessageCircle, Mail, ExternalLink, Instagram, Facebook, Linkedin, Youtube, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { ADDRESS, EMAIL, PHONE, PHONE_LINK, WHATSAPP, WA_LINK, MAPS_LINK } from "@/data/content";
import { api, track } from "@/lib/api";
import { IMAGES } from "@/data/images";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-offwhite placeholder:text-white/35 focus:border-gold focus:outline-none";

const SOCIALS = [
  { name: "Instagram", icon: Instagram }, { name: "Facebook", icon: Facebook },
  { name: "LinkedIn", icon: Linkedin }, { name: "YouTube", icon: Youtube },
];

export default function Contact() {
  const { t } = useLanguage();
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!data.name || data.name.trim().length < 2) errs.name = t("form.nameRequired");
    let digits = (data.phone || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    if (!digits) errs.phone = t("form.phoneRequired");
    else if (digits.length !== 10 || !/^[6-9]/.test(digits)) errs.phone = t("form.phoneInvalid");
    if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) errs.email = t("form.emailInvalid");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    try {
      await api.post("/enquiries", {
        name: data.name.trim(), phone: data.phone, email: data.email || null,
        form_type: "contact", message: data.message || null, company: data.company || "",
      });
      setStatus("success");
      track("contact_form_success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <PageHero eyebrow={t("contact.eyebrow")} title={t("contact.title")} image={IMAGES.int2} />
      <section className="bg-charcoal py-20 md:py-28" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-white/10 bg-white/[0.04] p-9 md:p-12">
              <h2 className="font-display text-2xl text-offwhite">D &amp; N Constructions</h2>
              <div className="mt-8 space-y-6 text-sm">
                <div>
                  <p className="eyebrow-dark mb-1.5">{t("contact.office")}</p>
                  <p className="flex items-start gap-2.5 text-white/75"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{ADDRESS}</p>
                  <a href={MAPS_LINK} target="_blank" rel="noreferrer" data-testid="contact-map-link"
                    className="mt-2.5 inline-flex items-center gap-1.5 pl-6 text-xs font-bold text-gold underline-offset-4 hover:underline">
                    {t("contact.map")} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div>
                  <p className="eyebrow-dark mb-1.5">{t("contact.phone")}</p>
                  <a href={PHONE_LINK} data-testid="contact-phone-link" className="flex items-center gap-2.5 text-white/75 transition-colors hover:text-gold">
                    <Phone className="h-4 w-4 text-gold" />{PHONE}
                  </a>
                </div>
                <div>
                  <p className="eyebrow-dark mb-1.5">{t("contact.whatsapp")}</p>
                  <a href={WA_LINK()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-2.5 text-white/75 transition-colors hover:text-gold">
                    <MessageCircle className="h-4 w-4 text-gold" />{WHATSAPP}
                  </a>
                </div>
                <div>
                  <p className="eyebrow-dark mb-1.5">{t("contact.email")}</p>
                  <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-2.5 break-all text-white/75 transition-colors hover:text-gold">
                    <Mail className="h-4 w-4 shrink-0 text-gold" />{EMAIL}
                  </a>
                </div>
              </div>
              <div className="mt-10 flex gap-3 border-t border-white/10 pt-8">
                {SOCIALS.map(({ name, icon: Icon }) => (
                  <button key={name} type="button" data-testid={`contact-social-${name.toLowerCase()}`}
                    title={`${name} — ${t("footer.socialSoon")}`} aria-label={`${name} (${t("footer.socialSoon")})`}
                    className="rounded-full border border-white/15 p-2.5 text-white/50 transition-colors hover:border-gold hover:text-gold">
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-9 md:p-12">
              {status === "success" ? (
                <div className="py-10 text-center" data-testid="contact-success">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
                  <p className="mx-auto mt-6 max-w-sm text-sm leading-relaxed text-white/70">{t("contact.success")}</p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="space-y-5" data-testid="contact-form">
                  <div>
                    <input data-testid="contact-name-input" className={inputCls} placeholder={`${t("form.name")} *`}
                      value={data.name || ""} onChange={(e) => set("name", e.target.value)} aria-invalid={!!errors.name} />
                    {errors.name && <p className="mt-1.5 text-xs text-red-400" role="alert">{errors.name}</p>}
                  </div>
                  <div>
                    <input data-testid="contact-phone-input" className={inputCls} inputMode="tel" placeholder={`${t("form.phone")} *`}
                      value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} aria-invalid={!!errors.phone} />
                    {errors.phone && <p className="mt-1.5 text-xs text-red-400" role="alert">{errors.phone}</p>}
                  </div>
                  <div>
                    <input data-testid="contact-email-input" className={inputCls} inputMode="email" placeholder={t("form.email")}
                      value={data.email || ""} onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} />
                    {errors.email && <p className="mt-1.5 text-xs text-red-400" role="alert">{errors.email}</p>}
                  </div>
                  <textarea data-testid="contact-message-input" className={`${inputCls} min-h-[110px] resize-none`} placeholder={t("form.message")}
                    value={data.message || ""} onChange={(e) => set("message", e.target.value)} />
                  <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
                    className="pointer-events-none absolute h-0 w-0 opacity-0"
                    value={data.company || ""} onChange={(e) => set("company", e.target.value)} />
                  {status === "error" && (
                    <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" role="alert" data-testid="contact-error">
                      {t("form.error")}
                    </p>
                  )}
                  <button type="submit" data-testid="contact-submit-button" disabled={status === "submitting"}
                    className="w-full rounded-full bg-gold px-7 py-4 text-sm font-bold text-charcoal transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-soft disabled:opacity-60">
                    {status === "submitting" ? t("form.sending") : t("contact.send")}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
